import { useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Shell } from './components/Shell'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { UsersPage } from './pages/UsersPage'
import { UserDetailPage } from './pages/UserDetailPage'
import { VerificationPage } from './pages/VerificationPage'
import { ProjectsPage } from './pages/ProjectsPage'
import { DealsPage } from './pages/DealsPage'
import { AuditPage, ProposalsPage, ReportsPage } from './pages/ListsPages'
import { PaymentsPage, SubscriptionsPage } from './pages/FinancePages'
import { LoginEventsPage, SecurityPage } from './pages/SecurityPages'
import {
  AnalyticsPage, CategoriesPage, ChatsPage, PromoPage,
} from './pages/Phase3Pages'
import { api, clearSession, getToken } from './lib/api'

const qc = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
})

function RequireAuth() {
  if (!getToken()) return <Navigate to="/login" replace />
  return <Outlet />
}

function AppLayout() {
  const nav = useNavigate()
  const [collapsed, setCollapsed] = useState(false)
  const [name, setName] = useState('Сотрудник')
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const me = await api<{ name: string }>('/admin/me')
        if (!cancelled) {
          setName(me.name)
          setReady(true)
        }
      } catch {
        if (!cancelled) {
          clearSession()
          nav('/login', { replace: true })
        }
      }
    })()
    return () => { cancelled = true }
  }, [nav])

  if (!ready) {
    return <div style={{ padding: 40, color: '#64748b' }}>Проверка сессии…</div>
  }

  return (
    <Shell collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} userName={name}>
      <Outlet />
    </Shell>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<RequireAuth />}>
            <Route element={<AppLayout />}>
              <Route index element={<DashboardPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:id" element={<UserDetailPage />} />
              <Route path="verification" element={<VerificationPage />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route path="projects/moderation" element={<ProjectsPage moderationOnly />} />
              <Route path="proposals" element={<ProposalsPage />} />
              <Route path="deals" element={<DealsPage />} />
              <Route path="payments" element={<PaymentsPage />} />
              <Route path="subscriptions" element={<SubscriptionsPage />} />
              <Route path="categories" element={<CategoriesPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="chats" element={<ChatsPage />} />
              <Route path="promo" element={<PromoPage />} />
              <Route path="moderation/reports" element={<ReportsPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="login-events" element={<LoginEventsPage />} />
              <Route path="security" element={<SecurityPage />} />
              <Route path="audit" element={<AuditPage />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
